import { Component, inject, DestroyRef, Input, ViewChild, Output, EventEmitter, OnInit,SimpleChanges, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectorRef } from '@angular/core';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { EnvironmentPaginatorComponent } from '../environment-paginator/environment-paginator.component';
import { EnvironmentTableComponent } from '../environment-table/environment-table.component';
import { translate } from '@jsverse/transloco';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { MatSort } from '@angular/material/sort';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { ColumnFilterComponent } from '../../instances-mgmt/column-filter/column-filter.component';
import { LocalConfiguration } from 'src/app/services/api/local-config.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { Attributes, Datazone, Permissions } from 'src/app/models/principal/permission';
import moment = require('moment');

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.LOCAL_CONFIG_APPLY_NONPII,
    action: Permissions.LOCAL_CONFIG_APPLY,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.LOCAL_CONFIG_APPLY_PII,
    action: Permissions.LOCAL_CONFIG_APPLY,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.LOCAL_CONFIG_APPLY_LIVE,
    action: Permissions.LOCAL_CONFIG_APPLY,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  },
  {
    key: PermissionKeys.LOCAL_CONFIG_READ_NONPII,
    action: Permissions.LOCAL_CONFIG_READ,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.LOCAL_CONFIG_READ_PII,
    action: Permissions.LOCAL_CONFIG_READ,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.LOCAL_CONFIG_READ_LIVE,
    action: Permissions.LOCAL_CONFIG_READ,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  }
];

@Component({
  selector: 'app-environment-local-configuration',
  templateUrl: './environment-local-configuration.component.html',
  styleUrl: './environment-local-configuration.component.scss',
  standalone: false
})
export class EnvironmentLocalConfigurationComponent implements OnInit, OnChanges{
  @ViewChild(EnvironmentPaginatorComponent) paginatorRef: EnvironmentPaginatorComponent;
  @ViewChild(EnvironmentTableComponent) tableRef: EnvironmentTableComponent;
  @ViewChild(MatSort) sort: MatSort;
  @Output() selectRow = new EventEmitter<any>();
  @Input() data: any;
  @Input() currentConfigVersion: string;
  @Output() updateEnvHeader = new EventEmitter<any>();

  
  private destroyRef = inject(DestroyRef);

  // Loading states
  showLoader: boolean = true;
  showApplyLoader: boolean = false;
  apiNotFound = false;
  localConfigDateOptions = [{ value: 'Created date', selected: false }];

  // Data
  localConfigData: any[] | null = null;;
  instanceId: string;
  instance: any;

  // Pagination
  pageIndex = 0;
  paginationSize = 10;

  // Search
  searchTerm: string = '';
  searchedResult = [];

  // Sorting
  sortInfo: any;

  // Filters
  selectedFilters: any[] = [];
  filteredConfigData: any[] = [];
  filterStartDate: any;
  filterEndDate: any;
  previousSelectedActions: any;
  localDropdownList: { title: string; }[];
  dropdownsList: any;
  date: any;

  selection: any = null;

  // Table columns configuration
  displayColumns = [
    {
      key: 'selected',
      name: '',
      type: 'radioSelection',
      sortable: false,
      visible: true,
      disable: false
    },
    {
      key: 'buildName',
      name: 'INSTANCE_DETAILS.BUILD_NAME',
      type: 'text',
      location: true,
      sortable: true,
      visible: true,
      shutdown: true,
      disable: true
    },
    {
      key: 'version',
      name: 'INSTANCE_DETAILS.VERSION',
      type: 'text',
      sortable: true,
      visible: true,
      disable: false
    },
    {
      key: 'createdAt',
      name: 'INSTANCE_DETAILS.CREATED_ON_UTC',
      type: 'date',
      sortable: true,
      visible: true,
      disable: false
    },
    {
      key: 'applyLocalConfig',  
      name: '',
      type: 'applyLocalConfig',  
      sortable: false,
      visible: true
    },
    {
      key: 'localConfigsettings',
      name: '',
      type: 'settings',
      visible: true,
      disable: false,
      sortable: false,
    }
  ];
  readonly ACTION_FAILED_STATES = [
      DeployStatus.FAILED, DeployStatus.TERMINATION_FAILED, DeployStatus.UPDATE_FAILED, DeployStatus.CONFIGURATION_FAILED, DeployStatus.INITIALIZE_FAILED,
      DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.TERMINATED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED
    ];
  readonly ACTION_NOT_ALLOWED_STATES = [DeployStatus.STARTING, DeployStatus.STOPPED, DeployStatus.STOPPING, DeployStatus.START_FAILED, DeployStatus.INITIALIZE_FAILED, DeployStatus.INITIALIZED, DeployStatus.INITIALIZING];
  readonly ACTION_DISABLED_STATUS = [DeployStatus.DEPLOYED, DeployStatus.CONFIGURING, DeployStatus.CONFIGURATION_FAILED, DeployStatus.CONFIGURED, DeployStatus.UPDATED, DeployStatus.TESTED, DeployStatus.UPDATE_FAILED, DeployStatus.TEST_FAILED, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, 
    DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.STOP_FAILED];
  readonly ACTION_ENABLED_STATUS = [DeployStatus.DEPLOYING, DeployStatus.TERMINATED, DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.OPERATION_INPROGRESS, DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING, DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING,
     DeployStatus.TERMINATING, DeployStatus.TESTING, DeployStatus.TENANT_AGENT_CREATION_IN_PROGRESS, DeployStatus.INITIALIZING, DeployStatus.STARTING, DeployStatus.STOPPING, DeployStatus.STARTING_AGENT, DeployStatus.STOPPING_AGENT,DeployStatus.TERMINATION_FAILED];
  permissionsMap: { [key: string]: boolean };
  applyingConfig : any;
  haveReadAccess : boolean;

  constructor(
    public dateTimeFormatService: DateTimeFormatService,
    private cdr: ChangeDetectorRef,
    private instanceService: InstancesService,
    private toastrService: CustomToastrService, private dialog: MatDialog,
    private activatedRoute: ActivatedRoute, private localConfig: LocalConfiguration,
    public datePipe: DatePipe,  private sidenavService: SidenavService, private permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.instance = this.data;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if(this.data) {
    this.haveReadAccess = ((this.data?.dataZone === DataZone.DEVELOPMENT && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_NONPII]) || (this.data?.dataZone === DataZone.SECURE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_PII]) || (this.data?.dataZone === DataZone.LIVE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_LIVE]));
    }
    this.instanceId = this.activatedRoute.snapshot.params.instanceId;
    this.localDropdownList =  [{ title: 'Date' }];
    this.loadLocalConfiguration();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  select(row): void {
    this.selection = row;
    this.filteredConfigData.forEach(config => {
      config.selected = config?.id === row?.id;
      config.showApply = config?.id === row?.id;
    });
  }

  modifyConfiguration() {
    const disableAction = this.ACTION_NOT_ALLOWED_STATES.includes(this.instance?.status);
    this.filteredConfigData = this.filteredConfigData.map(config => {
      return {
        ...config,
        selected: this.selection?.id === config?.id,
        showApply: this.selection?.id === config?.id,
        disableRetry: disableAction,
        havePermission: ((this.data?.dataZone === DataZone.DEVELOPMENT && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_APPLY_NONPII] && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_NONPII]) || (this.data?.dataZone === DataZone.SECURE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_APPLY_PII] && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_PII]) || (this.data?.dataZone === DataZone.LIVE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_APPLY_LIVE] && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_LIVE])),
        disableApply:this.applyingConfig ? true : (!this.ACTION_DISABLED_STATUS.includes(this.data?.status)) || this.ACTION_ENABLED_STATUS.includes(this.data?.status),
        disableSelection: this.applyingConfig
          ? config?.id !== this.selection?.id
          : (this.currentConfigVersion ? this.isLowerVersion(config?.version, this.currentConfigVersion) : false)
      };
    });
  }

  isLowerVersion(rowVersion: string, currentVersion: string): boolean {
    if (!currentVersion || !rowVersion) return false;
    const toNum = (v: string) => v?.split('.').map(Number)
      .reduce((acc, n, i) => acc + n * Math.pow(1000, 2 - i), 0);
    // strict less than — same version stays enabled
    return toNum(rowVersion) < toNum(currentVersion); 
  }

  loadLocalConfiguration(): void {
    this.showLoader = true;
    this.apiNotFound = false;
    this.instanceId = this.activatedRoute.snapshot.params.instanceId;
    this.localConfig.getLocalConfigurations(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        data => {
          this.showLoader = false;
          this.localConfigData = (data?.data || [])
            .map(item => ({
              ...item,
              buildName: item.artifactPath?.match(/([^/]+)\.zip$/)?.[1]
            }))
            .sort((a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          this.filteredConfigData = [...this.localConfigData];
          //  Restore selection from instance's applied localConfigurationId
          const isApplying = this.data?.localConfigStatus === 'applying';
          if (isApplying) {
            this.applyingConfig = true;
            // Restore selected row only during applying state
            if (this.data?.localConfigurationId) {
              const appliedConfig = this.localConfigData.find(test => test.id === this.data.localConfigurationId);
              if (appliedConfig) {
                this.selection = appliedConfig;
              }
            }
          } else {
            // Clear selection after success/failure
            this.applyingConfig = false;
            this.selection = null;
          }
          this.modifyConfiguration();
          if (this.searchTerm?.trim()) this.search(this.searchTerm);
          if (this.filterStartDate && this.filterEndDate) this.applyFilters();
        },
        (error) => {
          this.showLoader = false;
          if (error.httpStatusCode === 404) {
            this.apiNotFound = true;
          } else {
            setTimeout(() => {
              const title = translate('ERRORS.SERVICE_AVAILABILITY');
              const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
              this.toastrService.errMessage(message, title);
            }, 500);
          }
        });
  }

  // On page changed from header paginator
  onPageChange(event) {
    this.paginationSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.tableRef?.pageChanged(event);
  }

  // On page changed from table paginator
  onTablePageChanged(event) {
    this.pageIndex = event.pageIndex;
    this.paginationSize = event.pageSize;
  }

  // Resetting header paginator on sorting
  resetPaginator() {
    this.pageIndex = 0;
    if (this.paginatorRef) {
      this.paginatorRef.pageIndex = 0;
    }
  }

  // Reloading the data on refresh click
  refreshClick(): void {
    this.applyingConfig = false;
    this.updateEnvHeader.emit();
    this.getInstanceDetails();
    this.loadLocalConfiguration();
    setTimeout(() => {
    if (this.searchTerm?.trim()) {
      this.search(this.searchTerm);
    }
    if (this.filterStartDate && this.filterEndDate) {
      this.applyFilters();
    }
  }, 500);
  }

  // Assigning search term
  search(searchTerm?: string): void {
    this.searchTerm = searchTerm;

    // Start from date-filtered data if filter is active, else full data
    const baseData = (this.filterStartDate && this.filterEndDate)
      ? this.getDateFilteredData()
      : [...(this.localConfigData || [])];

    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredConfigData = baseData;
      this.modifyConfiguration();
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    this.filteredConfigData = baseData.filter(item =>
      item.buildName?.toLowerCase().includes(term)
    );
    this.modifyConfiguration();
  }

  //To get date-filtered data
  getDateFilteredData(): any[] {
    const startDate = new Date(this.filterStartDate).toDateString();
    const endDate = new Date(this.filterEndDate).toDateString();
    return (this.localConfigData || []).filter(item => {
      const itemDate = new Date(item.createdAt).toDateString();
      return new Date(itemDate) >= new Date(startDate) && new Date(itemDate) <= new Date(endDate);
    });
  }

  // Assigning search results when search is updated
  searchedItems(result): void {
    this.searchedResult = result;
  }

  // On click of Apply, It will trigger the function
  applyLocalConfigConfirm(selectedConfig: any): void {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      autoFocus: false,
      width: '566px',
      data: {
        title: translate('BLUEPRINT_SUMMARY.CONFIRM_LOCAL_APPLY.TITLE'),
        message: translate('BLUEPRINT_SUMMARY.CONFIRM_LOCAL_APPLY.MESSAGE1'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.showApplyLoader = true;  
          const instanceId = this.activatedRoute.snapshot.params['instanceId'];
          const localConfigurationId = selectedConfig?.id;
          this.localConfig
            .putLocalConfigApply(instanceId, localConfigurationId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (data) => {
                this.showApplyLoader = false;  
                this.applyingConfig = true;
                this.filteredConfigData.forEach(config => {
                  config.disableApply = true;
                  config.selected = config?.id === this.selection?.id;
                  config.disableSelection = config?.id !== this.selection?.id;
                });
                this.getLocalConfigResult(instanceId, data?.resultId); 
                this.updateEnvHeader.emit();
                this.getInstanceDetails();
                this.instanceService.setConfigApplied(instanceId);
                setTimeout(() => {
                  const title = translate('INSTANCE_SUMMARY.MENU.APPLY_LOCAL_CONFIG');
                  const message = translate('INSTANCE_SUMMARY.MENU.APPLY_LOCAL_CONFIG_SUCCESS');
                  this.toastrService.successMessage(message, title);
                }, 500);
              },
              error: (error) => {
                this.showApplyLoader = false;  
                this.applyingConfig = false;
                const errorMessage = typeof error === 'string' ? error : typeof error?.error === 'string' ? error.error : error;
                this.dialog.open(ErrorDialogComponent, {
                  minHeight: '342px',
                  width: '566px',
                  panelClass: 'flex-dialog',
                  data: {
                    title: translate('NOTIFICATIONS.APPLY_LOCAL_CONFIGURATION_FAILED'),
                    description: errorMessage,
                  }
                });
              }
            });
        }
      });
  }

  getLocalConfigResult(instanceId: string, resultId: string): void {
    this.localConfig.getLocalConfigResultId(instanceId, resultId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.data = { ...this.data, localConfigStatus: data?.status };
          const matchedConfig = this.filteredConfigData.find(config => config?.id === data?.instanceId);
          if (matchedConfig) {
            this.selection = matchedConfig;
            this.filteredConfigData.forEach(config => {
              config.disableApply = true;
              config.selected = config?.id === matchedConfig?.id;
              config.disableSelection = config?.id !== this.selection?.id; 
            });
          }
        },
        error: () => {
          this.applyingConfig = false;
          this.filteredConfigData.forEach(config => {
            config.disableSelection = false;
          });
        }
      });
  }
  
  applyFilters(): void {
    const dateFiltered = this.getDateFilteredData();

    if (this.searchTerm?.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredConfigData = dateFiltered.filter(item =>
        item.buildName?.toLowerCase().includes(term)
      );
    } else {
      this.filteredConfigData = dateFiltered;
    }
    this.modifyConfiguration();
  }

  // On click of Filter action
  filterClick(): void {
    this.localConfigDateOptions[0].selected = !!(this.filterStartDate && this.filterEndDate);
    const localConfigDate = this.localConfigDateOptions.map(item => ({ ...item }));
    const data = {
      name: 'LocalConfig',
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      filteredColumns: this.localDropdownList,
      filterOptions: localConfigDate,
      dateCreated: this.data?.dateCreated,
      startDateTest: localConfigDate[0].selected ? this.filterStartDate : '',  
      endDateTest: localConfigDate[0].selected ? this.filterEndDate : '',      
    };

    this.sidenavService.openInRightSidenav(ColumnFilterComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filteredColumns => {
        if (!filteredColumns) {
          if (!this.filterStartDate && !this.filterEndDate) {
            this.localConfigDateOptions[0].selected = false;
          }
          return;
        }
        const datePayload = filteredColumns[1]; 
        if (!datePayload?.filteredStartDate) {
          this.localConfigDateOptions.forEach(d => d.selected = false);
          this.localConfigDateOptions[0].selected = false;
          this.filterStartDate = undefined;
          this.filterEndDate = undefined;
          if (this.searchTerm?.trim()) {
            this.search(this.searchTerm);
          } else {
            this.filteredConfigData = [...this.localConfigData];
            this.modifyConfiguration();
          }
          return;
        }
        if (datePayload?.filteredStartDate && datePayload?.filteredEndDate) {
          this.localConfigDateOptions[0].selected = true;
          this.filterStartDate = datePayload.filteredStartDate;
          this.filterEndDate = datePayload.filteredEndDate;
        }
        this.applyFilters();
      });
  }

  // Cancel date filter
  cancelDateFilter(): void {
    this.filterStartDate = undefined;
    this.filterEndDate = undefined;
    this.localConfigDateOptions[0].selected = false;
    if (this.searchTerm?.trim()) {
      this.search(this.searchTerm);
    } else {
      this.filteredConfigData = [...this.localConfigData];
      this.modifyConfiguration();
    }
  }
  
  //fetch instance details
  getInstanceDetails(){
     this.instanceService.getInstanceDetails(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.data = data;
          this.instance = data;
        }
      })
  }
  
/**
 * Handles updates to @Input() data.
 * Ensures dependent logic runs only after data is available.
 * @param changes 
 */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data && this.permissionsMap) {
      this.haveReadAccess = ((this.data?.dataZone === DataZone.DEVELOPMENT && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_NONPII]) ||
      (this.data?.dataZone === DataZone.SECURE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_PII]) ||
      (this.data?.dataZone === DataZone.LIVE && this.permissionsMap[PermissionKeys.LOCAL_CONFIG_READ_LIVE]));
    }
    if (changes['currentConfigVersion'] && this.filteredConfigData?.length) {
      this.modifyConfiguration();
    }
  }
}